/**
 * A proposal as the owner decides it, in the chat and on the Proposals page. The figures always
 * come from the server: for a live proposal from its stored row (plan + the database's cap
 * check), for a trial card from the tool's computation. The assistant's title, summary and
 * reason are shown as its commentary next to them, never as the numbers being approved.
 */
import { useState } from 'react';
import {
  proposalFiguresFromRow,
  type ProposalFigures,
  type ProposalKind,
} from '../../supabase/functions/_shared/assistant/cards.ts';
import { useApp } from './context.tsx';
import type { Database } from './database.types.ts';
import { formatAed, formatDateTime } from './lib/format.ts';
import {
  CANCEL_KEYS,
  decideErrorKey,
  formatAedChange,
  KIND_KEYS,
  PROPOSAL_COLUMNS,
  STATUS_KEYS,
} from './lib/proposals.ts';
import type { MessageKey } from './lib/i18n.ts';
import { supabase } from './supabase.ts';

export type ProposalRow = Pick<
  Database['public']['Tables']['proposals']['Row'],
  | 'id'
  | 'kind'
  | 'status'
  | 'title'
  | 'summary'
  | 'rationale'
  | 'plan'
  | 'daily_change_fils'
  | 'month_change_fils'
  | 'projected_month_fils'
  | 'monthly_cap_fils'
  | 'auto_approved_stage'
  | 'cancel_reason'
  | 'expires_at'
  | 'created_at'
>;

/** Loads stored proposals by id (the chat) or the latest ones (the Proposals page). */
export async function loadProposalRows(ids: readonly string[] | null): Promise<ProposalRow[]> {
  const query = supabase.from('proposals').select(PROPOSAL_COLUMNS).neq('status', 'draft');
  const { data, error } = await (ids === null
    ? query.order('created_at', { ascending: false }).limit(50)
    : query.in('id', ids));
  if (error) throw new Error(`proposals: ${error.code}`);
  return data;
}

export function ProposalFiguresView(props: { readonly figures: ProposalFigures }) {
  const { t, language } = useApp();
  const f = props.figures;
  const arrow = language === 'ar' ? '←' : '→';
  return (
    <dl className="facts">
      {f.branches.map((b) => (
        <div key={b.code}>
          <dt>
            {t('figBranchDaily')} <bdi>{b.code}</bdi>
          </dt>
          <dd>
            <bdi>{formatAed(b.beforeFils, language)}</bdi> {arrow}{' '}
            <bdi>{formatAed(b.afterFils, language)}</bdi>
          </dd>
        </div>
      ))}
      {f.addedBranchCodes.length > 0 && (
        <div>
          <dt>{t('figBranches')}</dt>
          <dd>
            <bdi>{f.addedBranchCodes.join(', ')}</bdi>
          </dd>
        </div>
      )}
      <div>
        <dt>{t('figDailyChange')}</dt>
        <dd>
          <bdi>{formatAedChange(f.dailyChangeFils, language)}</bdi>
        </dd>
      </div>
      <div>
        <dt>{t('figMonthChange')}</dt>
        <dd>
          <bdi>{formatAedChange(f.monthChangeFils, language)}</bdi>
        </dd>
      </div>
      <div>
        <dt>{t('cardProjected')}</dt>
        <dd>{formatAed(f.projectedMonthFils, language)}</dd>
      </div>
      <div>
        <dt>{t('cardCap')}</dt>
        <dd>{formatAed(f.monthlyCapFils, language)}</dd>
      </div>
      <div>
        <dt>{t('figHeadroom')}</dt>
        <dd>{formatAed(f.monthlyCapFils - f.projectedMonthFils, language)}</dd>
      </div>
    </dl>
  );
}

/** The assistant's words about a proposal, labelled as its commentary. */
export function ProposalCommentary(props: {
  readonly summary: string;
  readonly rationale: string;
}) {
  const { t } = useApp();
  return (
    <details>
      <summary>{t('cardCommentary')}</summary>
      <p className="pre">{props.summary}</p>
      <p className="pre">{props.rationale}</p>
    </details>
  );
}

export const kindLabel = (kind: string): MessageKey | null =>
  kind in KIND_KEYS ? KIND_KEYS[kind as ProposalKind] : null;

/**
 * One stored proposal: kind, the figures from its row, the commentary, where it stands, and the
 * owner's buttons while it is pending (none when its figures are incomplete).
 */
export function StoredProposal(props: {
  readonly row: ProposalRow;
  /** Now, for the expiry; the page passes the time it loaded. */
  readonly now: number;
  readonly onDecided: () => void;
  readonly withNote: boolean;
}) {
  const { t, language, role } = useApp();
  const p = props.row;
  const figures = proposalFiguresFromRow(p);
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<
    { kind: 'idle' | 'busy' } | { kind: 'failed'; key: MessageKey }
  >({ kind: 'idle' });
  const pending = p.status === 'pending' && new Date(p.expires_at).getTime() > props.now;
  const statusKey =
    p.status === 'pending' && !pending ? 'propExpired' : (STATUS_KEYS[p.status] ?? 'propPending');
  const kind = kindLabel(p.kind);
  const cancelKey = p.cancel_reason === null ? undefined : CANCEL_KEYS[p.cancel_reason];

  const decide = async (decision: 'approved' | 'rejected') => {
    setOutcome({ kind: 'busy' });
    const { error } = await supabase.rpc('dashboard_decide_proposal', {
      p_proposal_id: p.id,
      p_decision: decision,
      ...(props.withNote && note.trim() ? { p_note: note.trim() } : {}),
    });
    if (error) {
      setOutcome({ kind: 'failed', key: decideErrorKey(error.code) });
      return;
    }
    setOutcome({ kind: 'idle' });
    props.onDecided();
  };

  const withdraw = async () => {
    setOutcome({ kind: 'busy' });
    const { error } = await supabase.rpc('dashboard_withdraw_approval', { p_proposal_id: p.id });
    if (error) {
      setOutcome({ kind: 'failed', key: 'withdrawFailed' });
      return;
    }
    setOutcome({ kind: 'idle' });
    props.onDecided();
  };

  return (
    <>
      <h3>
        {kind !== null && <span className="muted">{t(kind)}: </span>}
        {p.title}
      </h3>
      {figures === null ? (
        <p className="notice error">{t('figUnavailable')}</p>
      ) : (
        <ProposalFiguresView figures={figures} />
      )}
      <ProposalCommentary summary={p.summary} rationale={p.rationale} />
      {pending ? (
        <p className="small muted">
          {t('expires')}: {formatDateTime(p.expires_at, language)}
        </p>
      ) : (
        <p className="notice">
          {p.auto_approved_stage !== null && p.status === 'approved'
            ? t('cardAutoApproved')
            : t(statusKey)}
          {cancelKey !== undefined && <> {t(cancelKey)}</>}
        </p>
      )}
      {pending && figures !== null && role === 'owner' && (
        <div className="decide">
          {props.withNote && (
            <input
              type="text"
              maxLength={2000}
              placeholder={t('notePlaceholder')}
              aria-label={t('notePlaceholder')}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          )}
          <div className="actions">
            <button
              type="button"
              className="button primary"
              disabled={outcome.kind === 'busy'}
              onClick={() => void decide('approved')}
            >
              {t('approve')}
            </button>
            <button
              type="button"
              className="button quiet"
              disabled={outcome.kind === 'busy'}
              onClick={() => void decide('rejected')}
            >
              {t('reject')}
            </button>
          </div>
          {outcome.kind === 'failed' && (
            <p role="alert" className="error-text">
              {t(outcome.key)}
            </p>
          )}
        </div>
      )}
      {p.status === 'approved' && role === 'owner' && (
        <div className="decide">
          <p className="small muted">{t('withdrawHint')}</p>
          <div className="actions">
            <button
              type="button"
              className="button quiet"
              disabled={outcome.kind === 'busy'}
              onClick={() => void withdraw()}
            >
              {t('withdrawApproval')}
            </button>
          </div>
          {outcome.kind === 'failed' && (
            <p role="alert" className="error-text">
              {t(outcome.key)}
            </p>
          )}
        </div>
      )}
      {pending && role !== 'owner' && <p className="muted small">{t('viewerCannotDecide')}</p>}
    </>
  );
}
